import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { api } from '../../services/api';

export default function RegisterPage(): React.ReactElement {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);

  const registerSchema = z
    .object({
      first_name: z.string().min(1, tCommon('validation.required')),
      last_name: z.string().min(1, tCommon('validation.required')),
      email: z.string().email(tCommon('validation.email')),
      password: z.string().min(8, tCommon('validation.minLength', { min: 8 })),
      confirm_password: z.string().min(1, tCommon('validation.required')),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: tCommon('validation.passwordMatch'),
      path: ['confirm_password'],
    });

  type RegisterForm = z.infer<typeof registerSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterForm): Promise<void> => {
    setApiError('');
    try {
      await api.post('/v1/auth/register', {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setApiError(t('errors.emailTaken'));
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center pt-20 px-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body">
          <h2 className="card-title text-3xl font-bold justify-center mb-4">
            {t('register.title')}
          </h2>

          {success && (
            <div className="alert alert-success mb-4">
              <span>{t('register.success')}</span>
            </div>
          )}

          {apiError && (
            <div className="alert alert-error mb-4">
              <span>{apiError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('register.firstName')}</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.first_name ? 'input-error' : ''}`}
                {...register('first_name')}
              />
              {errors.first_name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.first_name.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('register.lastName')}</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.last_name ? 'input-error' : ''}`}
                {...register('last_name')}
              />
              {errors.last_name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.last_name.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('register.email')}</span>
              </label>
              <input
                type="email"
                className={`input input-bordered w-full ${errors.email ? 'input-error' : ''}`}
                {...register('email')}
              />
              {errors.email && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.email.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('register.password')}</span>
              </label>
              <input
                type="password"
                className={`input input-bordered w-full ${errors.password ? 'input-error' : ''}`}
                {...register('password')}
              />
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.password.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('register.confirmPassword')}</span>
              </label>
              <input
                type="password"
                className={`input input-bordered w-full ${errors.confirm_password ? 'input-error' : ''}`}
                {...register('confirm_password')}
              />
              {errors.confirm_password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.confirm_password.message}</span>
                </label>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full mt-2 mb-2"
              disabled={isSubmitting || success}
            >
              {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : t('register.submit')}
            </button>
          </form>

          <div className="text-center mt-2">
            <p className="text-sm">
              {t('register.hasAccount')}{' '}
              <RouterLink to="/login" className="link link-primary">
                {t('register.login')}
              </RouterLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
